/*
 * sps-posthog.js
 * -----------------------------------------------------------------------------
 * Social Print Studio — PostHog product analytics for the Shopify storefront.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The two editors — go.socialprintstudio.com (the new-web repo) and
 * editor. / new-editor. / calendar.socialprintstudio.com (the newEditor repo)
 * — report into ONE PostHog project (US Cloud, project 9012). This file adds
 * the storefront to it, so a customer's journey reads end to end: where they
 * landed, what they browsed, the product they went into an editor from, the
 * editor session itself, the cart, and — through the Shopify customer-events
 * pixel, see docs/posthog.md — the checkout and the order.
 *
 * ONE PERSON ACROSS SUBDOMAINS
 * ----------------------------
 * posthog-js keeps its anonymous id and its session id in a cookie on the
 * ROOT domain (`.socialprintstudio.com`): the SDK's default
 * `cross_subdomain_cookie: true` with its default `localStorage+cookie`
 * persistence. Every SPS subdomain therefore sees the same visitor, and when
 * an editor identifies the customer by email the storefront's earlier
 * browsing is already part of that person. Nothing here changes the cookie
 * name, the persistence mode or the domain — keep it that way, and keep the
 * same project key everywhere.
 *
 * WHAT IT RECORDS
 * ---------------
 *   - one $pageview per page load (the storefront is a multi-page site) and
 *     the matching $pageleave
 *   - clicks (autocapture: element text and position, never input values),
 *     rage clicks
 *   - unhandled JavaScript errors (PostHog error tracking), capped per page
 *     load so a tab stuck in a loop cannot flood it, without the browser's
 *     bare "a request failed" errors (Failed to fetch / Load failed), which
 *     were most of the volume and never pointed at anything fixable
 *   - Core Web Vitals (`$web_vitals`) for a sampled share of visits (theme
 *     setting; 10% by default) — page speed needs a sample, not every visit
 *   - failed adds to cart, which the theme catches itself: the add-to-cart
 *     button (assets/product-form.js) and the cart bridge, through
 *     `SPSPostHog.captureException`
 *   - `page_not_found` on the 404 template
 *   - a small storefront event catalog that does not depend on the page
 *     design, so the same numbers can be read before and after a redesign:
 *     the product page's main button (`product_cta_clicked`), links into an
 *     editor (`editor_link_clicked`), to a product or a collection
 *     (`product_link_clicked`, `collection_link_clicked`), to the App Store
 *     and the help centre, product options picked
 *     (`product_option_selected`), drop-downs opened (`accordion_opened`)
 *     and Klaviyo sign-ups (`newsletter_form_submitted`). Every event carries
 *     the page's context: `site_design` (v1 = the theme on main before the
 *     redesign, v2 = Theme V2), `template`, `product_handle` /
 *     `collection_handle`. See docs/posthog.md, "Storefront events".
 *   - session replay for a configurable share of visits (theme setting;
 *     off by default)
 *   - the shared marketing attribution (the `sps_attribution` cookie written
 *     by assets/sps-attribution.js) mirrored onto every event as utm_* and
 *     click-id properties, the way both editors do
 *   - who the visitor is: a signed-in Shopify customer, or the SPS editor
 *     session — sections/custom-header.liquid already asks the editor who
 *     the visitor is, and hands the email to `SPSPostHog.identify`
 *
 * Cart and checkout events do NOT come from here. Shopify publishes them
 * with a standard payload on every surface — checkout and the thank-you page
 * included, where theme code never runs — and the customer-events pixel in
 * docs/posthog-checkout-pixel.js forwards them under the same visitor.
 *
 * SAFETY
 * ------
 *   - Configured from theme settings (snippets/sps-posthog.liquid hands them
 *     over as data attributes on the script tag). No key, or the setting
 *     off: this file returns before doing anything.
 *   - The SDK is loaded after the window's `load` event (a fallback timer
 *     covers a page whose `load` never fires), so no analytics request can
 *     hold the page's own load event and nothing here is on the critical
 *     path. Calls made before then are queued and sent once it is up.
 *   - Every entry point is try/catch'd. Analytics never breaks the store.
 *
 * See docs/posthog.md.
 * -----------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Configuration — from the data attributes snippets/sps-posthog.liquid
  // renders on this script's own tag (theme settings + page context).
  // ---------------------------------------------------------------------------

  var script = document.getElementById('sps-posthog');
  var data = (script && script.dataset) || {};

  var KEY = String(data.key || '').trim();
  var HOST = String(data.host || '')
    .trim()
    .replace(/\/+$/, '');
  if (!KEY || !/^https:\/\/[a-z0-9.-]+(:\d+)?$/i.test(HOST)) return;
  // The theme editor's preview would report the merchant's own clicks.
  if (window.Shopify && window.Shopify.designMode) return;

  var AUTOCAPTURE = data.autocapture === 'true';
  // 0..1 — the share of visits that record a session replay.
  var REPLAY_RATE = Math.min(100, Math.max(0, Number(data.replaySampleRate) || 0)) / 100;
  // 0..1 — the share of visits that send Core Web Vitals (`$web_vitals`).
  // Blank (a theme without the setting yet) = the 10% default.
  var webVitalsSetting = String(data.webVitalsSampleRate == null ? '' : data.webVitalsSampleRate).trim();
  var WEB_VITALS_RATE = Math.min(100, Math.max(0, webVitalsSetting === '' ? 10 : Number(webVitalsSetting) || 0)) / 100;
  var PAGE_TYPE = String(data.pageType || '');
  var THEME_ROLE = String(data.themeRole || '');
  var CUSTOMER_EMAIL = String(data.customerEmail || '');
  var CUSTOMER_ID = String(data.customerId || '');
  // Page context for the storefront events (see "Storefront events" below).
  // `site_design` is fixed per branch in snippets/sps-posthog.liquid: v1 on
  // main before the redesign, v2 on theme-v2 — the axis for comparing them.
  var SITE_DESIGN = String(data.siteDesign || '');
  var THEME_NAME = String(data.themeName || '');
  var TEMPLATE = String(data.template || '');
  var PRODUCT_HANDLE = String(data.productHandle || '');
  var COLLECTION_HANDLE = String(data.collectionHandle || '');

  // Where the PostHog UI lives — only for the toolbar and "view in PostHog"
  // links; never where data goes. Same project as the editors.
  var UI_HOST = 'https://us.posthog.com';
  // Every event carries `app: "storefront"` (the editors send `new-web` and
  // `editor`), so one funnel can be broken down by app.
  var APP = 'storefront';
  var LOAD_FALLBACK_MS = 8000;
  var REPLAY_DECISION_KEY = 'ph_storefront_replay';
  var WEB_VITALS_DECISION_KEY = 'ph_storefront_web_vitals';
  // The attribution record shared with the editors (assets/sps-attribution.js).
  var ATTRIBUTION_COOKIE = 'sps_attribution';

  function debug() {
    try {
      console.debug.apply(console, ['[posthog]'].concat(Array.prototype.slice.call(arguments)));
    } catch (e) {
      /* nothing */
    }
  }

  // ---------------------------------------------------------------------------
  // The official PostHog loader stub (posthog.com/docs/getting-started/install).
  // It defines `window.posthog` with every public method queueing until
  // `/static/array.js` — the SDK, fetched from our own host — has loaded.
  // Do not edit; replace wholesale from the docs when updating.
  // ---------------------------------------------------------------------------

  /* prettier-ignore */
  /* eslint-disable */
  !function(t,e){var o,n,p,r;e.__SV||(window.posthog && window.posthog.__loaded)||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}p||((p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",p.onerror=function(){p=null},(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r));var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],Object.defineProperty(u,"toString",{configurable:!0,enumerable:!0,writable:!0,value:function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e}}),Object.defineProperty(u.people,"toString",{configurable:!0,enumerable:!0,writable:!0,value:function(){return u.toString(1)+".people (stub)"}}),o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagResult isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
  /* eslint-enable */

  // ---------------------------------------------------------------------------
  // Readiness — the SDK is up once array.js has run `init`. Until then, calls
  // wait here and run in order the moment it is (captures keep the time they
  // were asked for).
  // ---------------------------------------------------------------------------

  var pending = [];
  var PENDING_MAX = 100;
  // The SDK instance, from its `loaded` hook. Inside that hook `window.posthog`
  // is still the stub (array.js swaps it in only after `init` returns), so the
  // instance the hook hands over is what every call below uses.
  var instance = null;

  function sdk() {
    if (instance) return instance;
    var ph = window.posthog;
    return ph && ph.__loaded ? ph : null;
  }

  function ready() {
    return !!sdk();
  }

  function whenReady(fn) {
    var ph = sdk();
    if (ph) {
      try {
        fn(ph);
      } catch (e) {
        debug('call failed:', e);
      }
      return;
    }
    if (pending.length >= PENDING_MAX) pending.shift();
    pending.push(fn);
  }

  function flushPending() {
    var ph = sdk();
    if (!ph) return;
    var jobs = pending;
    pending = [];
    for (var i = 0; i < jobs.length; i++) {
      try {
        jobs[i](ph);
      } catch (e) {
        debug('queued call failed:', e);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Marketing attribution → super properties.
  //
  // The `sps_attribution` cookie (utm_* and paid-click ids; see
  // assets/sps-attribution.js) is mirrored onto every event, the same way
  // both editors do it, so "which campaign brought the people who did X"
  // is one breakdown in PostHog. The `_firstTouch` / `_landingPage` /
  // `_referrer` metadata stays out, as in the editors.
  // ---------------------------------------------------------------------------

  function readCookie(name) {
    var prefix = name + '=';
    var parts = (document.cookie || '').split(';');
    for (var i = 0; i < parts.length; i++) {
      var c = parts[i].trim();
      if (c.indexOf(prefix) !== 0) continue;
      try {
        return decodeURIComponent(c.substring(prefix.length));
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  function attributionProps() {
    var raw = readCookie(ATTRIBUTION_COOKIE);
    if (!raw) {
      try {
        raw = window.localStorage.getItem(ATTRIBUTION_COOKIE);
      } catch (e) {
        raw = null;
      }
    }
    if (!raw) return null;
    var attr;
    try {
      attr = JSON.parse(raw);
    } catch (e) {
      return null;
    }
    if (!attr || typeof attr !== 'object') return null;
    var props = {};
    var any = false;
    for (var key in attr) {
      if (!Object.prototype.hasOwnProperty.call(attr, key)) continue;
      if (key.charAt(0) === '_') continue;
      if (typeof attr[key] === 'string' && attr[key]) {
        props[key] = attr[key];
        any = true;
      }
    }
    return any ? props : null;
  }

  // ---------------------------------------------------------------------------
  // Identity.
  //
  // The email, lower-cased, is the distinct id — the same identity the
  // editors, Klaviyo and Shopify key on, so one person is one person across
  // the whole journey. Idempotent: nothing is sent when the visitor already
  // IS this person (the shared cookie carries an identity made in an editor
  // over to the storefront by itself).
  // ---------------------------------------------------------------------------

  function identify(email, customerId) {
    var id = String(email || '')
      .trim()
      .toLowerCase();
    if (!id || id.indexOf('@') === -1) return;
    whenReady(function (ph) {
      if (ph.get_distinct_id() === id) return;
      var props = { email: id };
      if (customerId) props.shopify_customer_id = String(customerId);
      ph.identify(id, props);
    });
  }

  // ---------------------------------------------------------------------------
  // Events. `capture` is the door for any theme code that wants to report a
  // moment; the storefront events below and page_not_found use it.
  // ---------------------------------------------------------------------------

  function capture(event, properties, options) {
    if (!event) return;
    var at = new Date();
    whenReady(function (ph) {
      var opts = { timestamp: at };
      for (var k in options || {}) {
        if (Object.prototype.hasOwnProperty.call(options, k)) opts[k] = options[k];
      }
      ph.capture(event, properties || {}, opts);
    });
  }

  // ---------------------------------------------------------------------------
  // Handled errors — `SPSPostHog.captureException(error, properties)`.
  //
  // For failures theme code catches itself, which error tracking would never
  // see otherwise (it records only UNHANDLED errors): a failed add to cart
  // (assets/product-form.js) and the cart bridge
  // (templates/page.newcartoperations.liquid).
  //
  // The SDK starts after the page's `load` event, and the cart bridge
  // redirects straight after a failure — usually before then. So until the
  // SDK is up, each error is also kept in sessionStorage, and the next page
  // in the tab (/cart, normally) sends what this one could not. When this
  // page does get to send it, it takes it out of that hand-over first, so
  // nothing is sent twice. Redirect timing is not touched.
  //
  // The cart bridge can fail before this (deferred) script has even run; it
  // then writes its entry into the hand-over itself — keep HANDOVER_KEY and
  // the entry format ({ id, name, message, stack, properties }) in step with
  // reportToPostHog there.
  // ---------------------------------------------------------------------------

  var HANDOVER_KEY = 'sps_posthog_errors';
  var HANDOVER_MAX = 5;
  // Hand-over entries made by this page (still queued here, not to resend).
  var handoverOwn = {};

  function readHandover() {
    try {
      var list = JSON.parse(window.sessionStorage.getItem(HANDOVER_KEY) || '[]');
      return Object.prototype.toString.call(list) === '[object Array]' ? list : [];
    } catch (e) {
      return [];
    }
  }

  function writeHandover(list) {
    try {
      if (list.length) {
        window.sessionStorage.setItem(HANDOVER_KEY, JSON.stringify(list.slice(-HANDOVER_MAX)));
      } else {
        window.sessionStorage.removeItem(HANDOVER_KEY);
      }
    } catch (e) {
      /* storage unavailable — the error still goes if the SDK comes up here */
    }
  }

  function dropFromHandover(id) {
    var list = readHandover();
    var kept = [];
    for (var i = 0; i < list.length; i++) {
      if (!list[i] || list[i].id !== id) kept.push(list[i]);
    }
    if (kept.length !== list.length) writeHandover(kept);
  }

  function captureException(error, properties) {
    try {
      var err = error instanceof Error ? error : new Error(String(error));
      var props = {};
      for (var k in properties || {}) {
        if (Object.prototype.hasOwnProperty.call(properties, k)) props[k] = properties[k];
      }
      // Where it failed, without the query string (the cart bridge's carries
      // the editor's cart key). The event's own $current_url is the page that
      // sends it, which is a later one when the error was handed over.
      props.failed_on_url = window.location.origin + window.location.pathname;
      if (ready()) {
        whenReady(function (ph) {
          ph.captureException(err, props);
        });
        return;
      }
      var id = new Date().getTime() + '-' + Math.random().toString(36).slice(2);
      handoverOwn[id] = true;
      var list = readHandover();
      list.push({ id: id, name: err.name, message: err.message, stack: err.stack, properties: props });
      writeHandover(list);
      whenReady(function (ph) {
        dropFromHandover(id);
        ph.captureException(err, props);
      });
    } catch (e) {
      debug('captureException failed:', e);
    }
  }

  // Errors an earlier page in this tab could not send (from onLoaded).
  function sendHandover(ph) {
    var list = readHandover();
    if (!list.length) return;
    var mine = [];
    for (var i = 0; i < list.length; i++) {
      var item = list[i];
      if (!item || typeof item !== 'object') continue;
      if (handoverOwn[item.id]) {
        mine.push(item);
        continue;
      }
      try {
        var err = new Error(String(item.message || ''));
        if (item.name) err.name = String(item.name);
        if (item.stack) err.stack = String(item.stack);
        var props = item.properties && typeof item.properties === 'object' ? item.properties : {};
        props.handed_over = true;
        ph.captureException(err, props);
      } catch (e) {
        debug('handed-over error failed:', e);
      }
    }
    writeHandover(mine);
  }

  // ---------------------------------------------------------------------------
  // Sampled per visit: session replay and Core Web Vitals.
  //
  // A coin toss per tab, kept in sessionStorage so a reload — or the next
  // page of the same visit — does not flip a visit in or out.
  //
  // Session replay: only when the theme setting asks for a share of visits.
  // Recording is switched off in `init` and started here for the sampled
  // share, so the project-level "record everything" setting (on for the new
  // editor) never applies to the storefront by accident.
  //
  // Core Web Vitals: a share of visits (10% by default). `$web_vitals` was
  // the storefront's second-biggest event — ~100K a week in October 2026, a
  // row or two per page view, more than its page views — and a page-speed
  // reading needs a sample, not every visit. Outside the sample `init` sets
  // `capture_performance: { web_vitals: false }`, which overrides the
  // project-level setting, so those visits never load the web-vitals code.
  // Inside it the project setting decides, as before.
  // ---------------------------------------------------------------------------

  function sampledIn(key, rate) {
    if (rate <= 0) return false;
    if (rate >= 1) return true;
    var decision = null;
    try {
      decision = window.sessionStorage.getItem(key);
    } catch (e) {
      /* storage unavailable — decide fresh */
    }
    if (decision === null) {
      decision = Math.random() < rate ? '1' : '0';
      try {
        window.sessionStorage.setItem(key, decision);
      } catch (e) {
        /* nothing */
      }
    }
    return decision === '1';
  }

  function replayWanted() {
    return sampledIn(REPLAY_DECISION_KEY, REPLAY_RATE);
  }

  function webVitalsWanted() {
    return sampledIn(WEB_VITALS_DECISION_KEY, WEB_VITALS_RATE);
  }

  // ---------------------------------------------------------------------------
  // Error volume cap, per page load (`before_send`).
  //
  // A tab left open for days can hit the same failure every few seconds for
  // as long as it stays open. In September 2026 one visitor's two Firefox tabs
  // sent ~41,000 identical "NetworkError when attempting to fetch resource."
  // in a week: a third-party app's fetch wrapper turned every failed request
  // on the page — Shopify's own 5-second telemetry included — into an
  // unhandled rejection (docs/posthog.md, "Error volume"). The SDK's own
  // limiter did not stop them: it is per exception TYPE (a loop also mutes
  // unrelated errors of the same type) and it refills over time, so a loop
  // that pauses — a background tab, a sleeping laptop — gets a fresh batch
  // after every pause.
  //
  // So, per page load: at most EXCEPTION_REPEAT_MAX of the same exception
  // (type + message + where it was thrown), and EXCEPTION_PAGE_MAX of any
  // kind. The first ones are always sent, so a real failure — a fetch error
  // included — still shows up in error tracking; only the echo is dropped.
  // Normal page loads come nowhere near either limit (worst seen: 5 distinct
  // errors on one page).
  //
  // Network noise is dropped first (since 2026-10-25): an UNHANDLED error
  // whose whole message is the browser's "a request failed" — Chrome's
  // "Failed to fetch", Safari's "Load failed", Firefox's "NetworkError when
  // attempting to fetch resource." It was ~90% of the storefront's errors
  // (~60K a month, 2026-10), from visitors' own connections, ad blockers
  // and third-party scripts, never with anything to fix, and error tracking
  // is billed per exception. Our own reports are never dropped: a failed add
  // to cart or cart-bridge step goes through `captureException` (above),
  // which PostHog marks handled and which carries `failed_on_url`.
  // ---------------------------------------------------------------------------

  var NETWORK_NOISE = {
    'Failed to fetch': true,
    'Load failed': true,
    'NetworkError when attempting to fetch resource.': true,
    'NetworkError: Load failed': true,
  };

  function isNetworkNoise(event, ex) {
    if (!ex || !Object.prototype.hasOwnProperty.call(NETWORK_NOISE, String(ex.value || ''))) return false;
    if (ex.mechanism && ex.mechanism.handled === true) return false;
    return !(event.properties && event.properties.failed_on_url);
  }

  var EXCEPTION_REPEAT_MAX = 5;
  var EXCEPTION_PAGE_MAX = 50;
  var exceptionCounts = Object.create(null);
  var exceptionTotal = 0;

  // Type, message and the stack's call sites: two failures with the same
  // message from different code are counted apart. No stack (Firefox and
  // Safari give failed fetches none) means type and message alone.
  function exceptionKey(ex) {
    var frames = (ex && ex.stacktrace && ex.stacktrace.frames) || [];
    var sites = [];
    for (var i = 0; i < frames.length; i++) {
      var f = frames[i] || {};
      sites.push([f.filename, f['function'], f.lineno, f.colno].join(':'));
    }
    return [(ex && ex.type) || '', (ex && ex.value) || '', sites.join('|')].join('\n');
  }

  function capExceptions(event) {
    // The SDK drops an event when before_send throws — never let the cap
    // cost an event; when in doubt, send it.
    try {
      if (!event || event.event !== '$exception') return event;
      var list = event.properties && event.properties.$exception_list;
      if (isNetworkNoise(event, list && list[0])) return null;
      if (exceptionTotal >= EXCEPTION_PAGE_MAX) return null;
      var key = exceptionKey(list && list[0]);
      var seen = exceptionCounts[key] || 0;
      if (seen >= EXCEPTION_REPEAT_MAX) return null;
      exceptionCounts[key] = seen + 1;
      exceptionTotal++;
    } catch (e) {
      debug('exception cap failed:', e);
    }
    return event;
  }

  // ---------------------------------------------------------------------------
  // Storefront events — what visitors do on the store, named by intent and
  // NOT by markup, so the same events (same names, same properties) come
  // from the old design and Theme V2 alike and the two can be compared:
  //
  //   product_cta_clicked      the product page's main button (#order-button)
  //   editor_link_clicked      any link into an ordering flow: an editor
  //                            (editor., new-editor., calendar., go.) or the
  //                            older options site (web.)
  //   product_link_clicked     a link to another product (cards, menus, rails)
  //   collection_link_clicked  a link to a collection
  //   app_store_link_clicked   a link to the iOS app
  //   help_link_clicked        a link to the help centre
  //   product_option_selected  an option picked on a product page
  //   accordion_opened         a drop-down opened by the visitor (product
  //                            details, FAQ), not menus
  //   newsletter_form_submitted a Klaviyo form sent
  //
  // `link_area` is the theme section the click came from (its id without the
  // template prefix and Shopify's random suffix: `main`, `product-grid`,
  // `custom-header`, `custom-related-products-cards-with-sliders`, …) and
  // `position` is the 1-based place in its list, when it is in one.
  //
  // Page context is registered as super properties in onLoaded:
  // `site_design`, `theme_name`, `template`, `customer_signed_in`, and on the
  // pages they belong to `product_handle` / `collection_handle` (taken off
  // again on other pages — super properties persist between pages).
  //
  // A click that leaves the page is sent at once (sendBeacon). One made
  // before the SDK is up (it starts after the window's load event) goes
  // straight to the capture endpoint under the visitor's ids from the SDK's
  // own cookie (sendEarly) — otherwise a quick click on "Get started" would
  // be lost, and how quickly a page loads would bias the click-through rate
  // between two designs. A first-ever visit has no cookie yet; such a click is
  // queued and sent only if the page stays.
  // ---------------------------------------------------------------------------

  // Super properties set from each page's own context. Super properties persist
  // between pages, so one this page does not have is taken off, never left over
  // from the page before.
  var PAGE_SCOPED = ['site_design', 'theme_name', 'template', 'product_handle', 'collection_handle'];

  function pageContext() {
    var context = {};
    if (SITE_DESIGN) context.site_design = SITE_DESIGN;
    if (THEME_NAME) context.theme_name = THEME_NAME;
    if (TEMPLATE) context.template = TEMPLATE;
    context.customer_signed_in = !!CUSTOMER_EMAIL;
    if (PRODUCT_HANDLE) context.product_handle = PRODUCT_HANDLE;
    if (COLLECTION_HANDLE) context.collection_handle = COLLECTION_HANDLE;
    return context;
  }

  function registerPageContext(ph, context) {
    var page = pageContext();
    for (var k in page) {
      if (Object.prototype.hasOwnProperty.call(page, k)) context[k] = page[k];
    }
    for (var i = 0; i < PAGE_SCOPED.length; i++) {
      if (!(PAGE_SCOPED[i] in page)) ph.unregister(PAGE_SCOPED[i]);
    }
  }

  var EDITOR_HOST = /^(editor|new-editor|calendar|go|web)\.socialprintstudio\.com$/i;
  var STORE_HOST = /^(www\.|shop\.)?socialprintstudio\.com$/i;
  var APP_STORE_HOST = /(^|\.)apps\.apple\.com$/i;
  var HELP_HOST = /^help\.socialprintstudio\.com$/i;
  var SDK_COOKIE = 'ph_' + KEY + '_posthog';
  // posthog-js session rules (the same ones the checkout pixel follows): a
  // session ends after 30 idle minutes, or 24 hours after it began.
  var SESSION_IDLE_MS = 30 * 60 * 1000;
  var SESSION_MAX_MS = 24 * 60 * 60 * 1000;

  // The text as written, not as styled: textContent, so a design that sets
  // text-transform (the old one uppercases buttons) reports the same words.
  function textOf(el) {
    if (!el) return '';
    var t = String(el.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!t) t = String(el.getAttribute('aria-label') || '').trim();
    if (!t) {
      var img = el.querySelector && el.querySelector('img[alt]');
      if (img) t = String(img.getAttribute('alt') || '').trim();
    }
    return t.slice(0, 80);
  }

  // The theme section an element is in, by its key: `main`, `product-grid`,
  // `custom-header`, `custom-faq`… (Shopify adds a random 6-character suffix
  // to keys made in the theme editor, e.g. custom_faq_CTygwE; it is dropped.)
  function areaOf(el) {
    var section = el.closest && el.closest('.shopify-section');
    if (!section || !section.id) {
      if (el.closest && el.closest('footer')) return 'footer';
      if (el.closest && el.closest('header')) return 'header';
      return 'page';
    }
    return section.id
      .replace(/^shopify-section-/, '')
      .replace(/^(template|sections)--\d+__/, '')
      .replace(/_(?=[A-Za-z0-9]*[A-Z0-9])[A-Za-z0-9]{6}$/, '')
      .replace(/_/g, '-')
      .slice(0, 60);
  }

  // 1-based place of the element's list item among its siblings, or null.
  function positionOf(el) {
    var li = el.closest && el.closest('li');
    if (!li || !li.parentElement) return null;
    var kids = li.parentElement.children;
    var n = 0;
    for (var i = 0; i < kids.length; i++) {
      if (kids[i].tagName !== 'LI') continue;
      n++;
      if (kids[i] === li) return n;
    }
    return null;
  }

  function safeDecode(text) {
    try {
      return decodeURIComponent(text);
    } catch (e) {
      return text;
    }
  }

  // What a link points at. The path only: query strings can carry cart keys.
  function linkInfo(a) {
    var url;
    try {
      url = new URL(a.getAttribute('href') || '', window.location.href);
    } catch (e) {
      return null;
    }
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    var host = url.hostname.toLowerCase();
    var info = { host: host, path: url.pathname, kind: 'external', handle: '' };
    if (EDITOR_HOST.test(host)) {
      info.kind = 'editor';
    } else if (host === window.location.hostname.toLowerCase() || STORE_HOST.test(host)) {
      var product = url.pathname.match(/\/products\/([^/?#]+)/);
      var collection = url.pathname.match(/^\/collections\/([^/?#]+)\/?$/);
      if (product) {
        info.kind = 'product';
        info.handle = safeDecode(product[1]);
      } else if (collection) {
        info.kind = 'collection';
        info.handle = safeDecode(collection[1]);
      } else {
        info.kind = 'store';
      }
    } else if (APP_STORE_HOST.test(host)) {
      info.kind = 'app_store';
    } else if (HELP_HOST.test(host)) {
      info.kind = 'help';
    }
    return info;
  }

  // The option's name: its fieldset's legend, or the input name Dawn builds
  // as "<option name>-<position>" (with whitespace from the Liquid around it).
  function optionName(el) {
    var legend = el.closest && el.closest('fieldset') && el.closest('fieldset').querySelector('legend');
    var name = legend && legend.firstChild && legend.firstChild.nodeType === 3 ? legend.firstChild.nodeValue : '';
    name = String(name || '')
      .replace(/\s+/g, ' ')
      .replace(/:\s*$/, '')
      .trim();
    if (!name) {
      name = String(el.name || '')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/-\d+$/, '');
    }
    return name.slice(0, 60);
  }

  // "Size: 4x6 / Finish: Matte" — the options showing when the button is pressed.
  function selectedOptions() {
    var picker = document.querySelector('variant-selects');
    if (!picker) return null;
    var parts = [];
    var fields = picker.querySelectorAll('input[type="radio"]:checked, select');
    for (var i = 0; i < fields.length; i++) {
      parts.push(optionName(fields[i]) + ': ' + String(fields[i].value || ''));
    }
    return parts.length ? parts.join(' / ').slice(0, 200) : null;
  }

  // ---- sending a click that leaves the page --------------------------------

  function uuidv7() {
    var bytes = [];
    for (var i = 0; i < 10; i++) bytes.push(Math.floor(Math.random() * 256));
    try {
      if (window.crypto && window.crypto.getRandomValues) {
        var arr = new Uint8Array(10);
        window.crypto.getRandomValues(arr);
        bytes = Array.prototype.slice.call(arr);
      }
    } catch (e) {
      /* Math.random it is */
    }
    var hex = function (b) {
      return (b < 16 ? '0' : '') + b.toString(16);
    };
    var r = bytes.map(hex).join('');
    var t = ('000000000000' + new Date().getTime().toString(16)).slice(-12);
    return (
      t.slice(0, 8) + '-' + t.slice(8, 12) + '-7' + r.slice(2, 5) + '-' +
      hex((bytes[0] & 0x3f) | 0x80) + r.slice(5, 7) + '-' + r.slice(7, 19)
    );
  }

  function sdkState() {
    var raw = readCookie(SDK_COOKIE);
    if (!raw) return null;
    var forms = [raw];
    try {
      forms.push(decodeURIComponent(raw));
    } catch (e) {
      /* already decoded */
    }
    for (var i = 0; i < forms.length; i++) {
      try {
        var state = JSON.parse(forms[i]);
        if (state && typeof state === 'object') return state;
      } catch (e) {
        /* try the next form */
      }
    }
    return null;
  }

  // Before the SDK is up: post the event to the capture endpoint ourselves,
  // under the ids the SDK keeps in its cookie, with the properties the SDK
  // would have added for this page. False when there is no cookie yet.
  // The SDK drops events from bots and automated browsers; the early path
  // must not let them back in.
  var BOT_UA = /bot|crawl|spider|slurp|headless|lighthouse|pagespeed|preview|phantom|puppeteer|playwright/i;

  function sendEarly(event, properties) {
    try {
      if (typeof window.fetch !== 'function') return false;
      if (navigator.webdriver || BOT_UA.test(navigator.userAgent || '')) return false;
      var brands = (navigator.userAgentData && navigator.userAgentData.brands) || [];
      for (var b = 0; b < brands.length; b++) {
        if (BOT_UA.test((brands[b] && brands[b].brand) || '')) return false;
      }
      var state = sdkState();
      if (!state || typeof state.distinct_id !== 'string' || !state.distinct_id) return false;
      var props = {
        app: APP,
        $lib: 'web',
        page_type: PAGE_TYPE,
        $current_url: window.location.href,
        $host: window.location.host,
        $pathname: window.location.pathname,
        $referrer: document.referrer || '$direct',
        $viewport_width: window.innerWidth,
        $viewport_height: window.innerHeight,
        $process_person_profile: state.$user_state === 'identified',
        sent_before_sdk: true,
      };
      if (THEME_ROLE) props.theme_role = THEME_ROLE;
      if (window.screen) {
        props.$screen_width = window.screen.width;
        props.$screen_height = window.screen.height;
      }
      if (typeof state.$device_id === 'string') props.$device_id = state.$device_id;
      // $sesid = [last activity, session id, session start]
      var ses = state.$sesid;
      if (ses && typeof ses === 'object' && typeof ses[1] === 'string' && ses[1]) {
        var now = new Date().getTime();
        var activity = Number(ses[0]) || 0;
        var started = Number(ses[2]) || activity;
        if (now - activity < SESSION_IDLE_MS && now - started < SESSION_MAX_MS) props.$session_id = ses[1];
      }
      var extra = [attributionProps() || {}, pageContext(), properties || {}];
      for (var j = 0; j < extra.length; j++) {
        for (var k in extra[j]) {
          if (Object.prototype.hasOwnProperty.call(extra[j], k)) props[k] = extra[j][k];
        }
      }
      window
        .fetch(HOST + '/e/', {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          keepalive: true,
          headers: { 'Content-Type': 'text/plain' },
          body: JSON.stringify({
            api_key: KEY,
            event: event,
            distinct_id: state.distinct_id,
            uuid: uuidv7(),
            timestamp: new Date().toISOString(),
            properties: props,
          }),
        })
        .catch(function () {
          /* nothing */
        });
      return true;
    } catch (e) {
      debug('early send failed:', e);
      return false;
    }
  }

  function captureClick(event, properties, leaving) {
    if (!leaving) {
      capture(event, properties);
    } else if (ready()) {
      capture(event, properties, { send_instantly: true, transport: 'sendBeacon' });
    } else if (!sendEarly(event, properties)) {
      capture(event, properties);
    }
  }

  // ---- listeners -------------------------------------------------------------

  // Bubble phase on the document: the theme's own handlers have run, so a
  // click they cancelled (the "not on mobile" notice on some products) is
  // known — event.defaultPrevented.
  function onClick(e) {
    try {
      if (!e.isTrusted || !e.target || !e.target.closest) return;
      var cta = e.target.closest('#order-button');
      var a = e.target.closest('a[href]');
      var info = a ? linkInfo(a) : null;
      var newTab = !!(a && (a.target === '_blank' || e.metaKey || e.ctrlKey || e.shiftKey));
      var leaving = !!(a && info && !e.defaultPrevented && !newTab);

      if (cta) {
        var ctaProps = {
          cta_text: textOf(cta),
          destination_kind: info ? info.kind : 'none',
          destination_host: info ? info.host : null,
          destination_path: info ? info.path : null,
          mobile_notice: !!e.defaultPrevented,
          selected_options: selectedOptions(),
        };
        captureClick('product_cta_clicked', ctaProps, leaving);
      }
      if (!a || !info) return;

      var area = areaOf(a);
      if (info.kind === 'editor') {
        if (e.defaultPrevented) return;
        captureClick(
          'editor_link_clicked',
          {
            destination_host: info.host,
            destination_path: info.path,
            link_text: textOf(a),
            link_area: area,
            from_product_cta: !!cta,
          },
          leaving
        );
      } else if (info.kind === 'product') {
        // A link back into the same product (#reviews, a gallery anchor) is not browsing.
        if (info.handle === PRODUCT_HANDLE) return;
        captureClick(
          'product_link_clicked',
          {
            destination_handle: info.handle,
            link_area: area,
            position: positionOf(a),
            link_text: textOf(a),
          },
          leaving
        );
      } else if (info.kind === 'collection') {
        captureClick(
          'collection_link_clicked',
          { destination_handle: info.handle, link_area: area, link_text: textOf(a) },
          leaving
        );
      } else if (info.kind === 'app_store') {
        captureClick('app_store_link_clicked', { link_area: area }, leaving);
      } else if (info.kind === 'help') {
        captureClick('help_link_clicked', { destination_path: info.path, link_area: area }, leaving);
      }
    } catch (err) {
      debug('click event failed:', err);
    }
  }

  // An option picked on a product page (Dawn's variant picker, both pill and
  // drop-down forms). Only real changes by the visitor.
  function onChange(e) {
    try {
      var el = e.target;
      if (!e.isTrusted || !el || !el.closest || !el.closest('variant-selects')) return;
      if (el.type === 'radio' && !el.checked) return;
      capture('product_option_selected', {
        option_name: optionName(el),
        option_value: String(el.value || '').slice(0, 80),
      });
    } catch (err) {
      debug('option event failed:', err);
    }
  }

  // A drop-down the visitor opened. `toggle` also fires for a <details> that
  // renders open (a product's default-open section), so only a toggle right
  // after a click on that drop-down's own summary counts. Menus (header,
  // footer) are left to autocapture.
  var lastSummary = null;
  var lastSummaryAt = 0;

  function onSummaryClick(e) {
    try {
      var summary = e.target && e.target.closest && e.target.closest('summary');
      lastSummary = summary ? summary.parentElement : null;
      lastSummaryAt = new Date().getTime();
    } catch (err) {
      /* nothing */
    }
  }

  function onToggle(e) {
    try {
      var d = e.target;
      if (!d || d.tagName !== 'DETAILS' || !d.open) return;
      if (d !== lastSummary || new Date().getTime() - lastSummaryAt > 1500) return;
      lastSummary = null;
      var area = areaOf(d);
      if (/header|footer|menu/.test(area)) return;
      var summary = d.querySelector('summary');
      capture('accordion_opened', { title: textOf(summary || d), link_area: area });
    } catch (err) {
      debug('accordion event failed:', err);
    }
  }

  // Klaviyo forms (the popup and the embedded sign-ups) announce a submit on
  // the document; sections/custom-header.liquid identifies the email from the
  // same event.
  function onKlaviyo(e) {
    try {
      if (!e.detail || e.detail.type !== 'submit') return;
      capture('newsletter_form_submitted', { form_id: String(e.detail.formId || '') });
    } catch (err) {
      debug('newsletter event failed:', err);
    }
  }

  try {
    document.addEventListener('click', onSummaryClick, true);
    document.addEventListener('click', onClick, false);
    document.addEventListener('change', onChange, false);
    document.addEventListener('toggle', onToggle, true);
    document.addEventListener('klaviyoForms', onKlaviyo, false);
  } catch (e) {
    debug('event listeners failed:', e);
  }

  // ---------------------------------------------------------------------------
  // Start.
  // ---------------------------------------------------------------------------

  // Runs from the SDK's `loaded` hook — BEFORE its first $pageview, so the
  // super properties below are on it.
  function onLoaded(ph) {
    instance = ph;
    try {
      var context = {
        app: APP,
        // Shopify's page type (product, collection, cart, index, page, 404…).
        page_type: PAGE_TYPE,
      };
      // `main` = the live theme. Preview / unpublished themes run on the
      // same domain, so this is how test traffic is told apart. (Liquid's
      // `theme` object is deprecated; if it ever renders empty, nothing is
      // registered rather than a blank.)
      if (THEME_ROLE) context.theme_role = THEME_ROLE;
      registerPageContext(ph, context);
      ph.register(context);
      var attribution = attributionProps();
      if (attribution) ph.register(attribution);
      if (CUSTOMER_EMAIL) identify(CUSTOMER_EMAIL, CUSTOMER_ID);
      if (PAGE_TYPE === '404') {
        capture('page_not_found', {
          path: window.location.pathname,
          referrer: document.referrer || null,
        });
      }
      if (replayWanted()) ph.startSessionRecording();
      sendHandover(ph);
    } catch (e) {
      debug('loaded hook failed:', e);
    }
    flushPending();
  }

  var started = false;

  function start() {
    if (started) return;
    started = true;
    try {
      window.posthog.init(KEY, {
        api_host: HOST,
        ui_host: UI_HOST,
        // The SDK's current default behaviours, pinned by date so an upgrade
        // never changes them under us — the SAME value as both editors, which
        // matters here: since '2026-08-29' the shared cross-subdomain cookie
        // wins over this subdomain's localStorage when the two disagree
        // about who the visitor is, and all three apps must agree on that.
        defaults: '2026-08-30',

        // ── What gets recorded ─────────────────────────────────────────
        // One $pageview per page load. The preset's `history_change` would
        // also count Dawn's query-only URL rewrites (a variant picked, a
        // collection filtered) as page views; those clicks are recorded by
        // autocapture already.
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: AUTOCAPTURE,
        capture_dead_clicks: false,
        // Unhandled errors and promise rejections → PostHog error tracking,
        // capped per page load (capExceptions, above).
        capture_exceptions: true,
        before_send: capExceptions,
        // Core Web Vitals for the sampled share of visits only (above).
        // `undefined` = the project-level setting decides.
        capture_performance: webVitalsWanted() ? undefined : { web_vitals: false },
        // A person profile only once an email is known (identify). Anonymous
        // browsing is still tracked, as events — the cheaper tier and all a
        // funnel needs. Same as both editors.
        person_profiles: 'identified_only',
        disable_surveys: true,

        // ── Session replay ─────────────────────────────────────────────
        // Off unless the theme setting samples it (replayWanted, above).
        // Inputs stay masked — the SDK default; the editors unmask theirs
        // to watch the product being used, the storefront has no need.
        disable_session_recording: true,

        loaded: onLoaded,
      });
    } catch (e) {
      debug('init failed:', e);
    }
  }

  // After the window's `load`, at once when it has already fired; a fallback
  // timer covers a page whose `load` is held up by a slow third party.
  if (document.readyState === 'complete') {
    start();
  } else {
    window.addEventListener('load', start);
    setTimeout(start, LOAD_FALLBACK_MS);
  }

  // The door for the rest of the theme (sections/custom-header.liquid hands
  // the SPS editor session's email to `identify`; assets/product-form.js and
  // the cart bridge report failed adds to `captureException`). Only defined
  // when PostHog is on — callers guard with `if (window.SPSPostHog)`.
  window.SPSPostHog = {
    identify: identify,
    capture: capture,
    captureException: captureException,
    ready: ready,
  };
})();
